<template>
    <div>
        <aside v-if="pdf.pending" class="work-pdf-pending" role="status">Recupero del PDF… <button type="button" @click="closePdf">Annulla apertura PDF</button></aside>
        <work-pdf-dialog v-if="pdf.mounted" ref="dialog" :active="pdf.overlay" :generation="pdf.instanceKey" :url="pdf.url" :prepared="pdf.prepared" :context="pdf.context" :resume-state="pdf.resumeState" :title="pdf.title" :error="pdf.error" :allow-print="!pdf.item || pdf.item.allowPrint !== false" :allow-download="!pdf.item || pdf.item.allowDownload !== false" @open="PdfopenHandler" @rendered="PdfRendered" @close="closePdf" @retry="retryPdf" @saved="savedPdf" @original="showPdf({ ...pdf.item, originalPdf: $event })" />
    </div>
</template>

<script>
import WorkPdfDialog from "./WorkPdfDialog.vue";
import { loadPdfEngine, preparePdfDocument, clearPdfDocuments, warmPdfPage, renderPdfPreview } from "./pdfEngine";
import { resolvePdfSource } from "./resolvePdfSource.mjs";
import { readScanOrientation } from "./pdfOrientation.mjs";
import { bus } from "../../../main";

export default {
    name: "WorkPdfHost",
    components: { WorkPdfDialog },
    created() { bus.$on("prefetch-pdf", this.prefetchPdf); },
    beforeDestroy() { bus.$off("prefetch-pdf", this.prefetchPdf); this._previewAbort?.abort(); this.closePdf(); clearPdfDocuments(); },
    data: () => ({
        pdf: {
            pending: false,
            prepared: null,
            mounted: false,
            overlay: false,
            url: "#",
            title: "",
            error: "",
            item: null,
            context: null,
            instanceKey: 0,
            revokeOnClose: false,
            resumeState:null,
        },
        pdfloading: false,
    }),
    methods: {
        async prefetchPdf(item) {
            if (navigator.connection?.saveData || ["slow-2g", "2g"].includes(navigator.connection?.effectiveType)) return;
            const key = `${item.articolo}:${item.disegno}`;
            if (this.pdf.overlay || this.pdf.pending || (key === this._prefetchKey && Date.now() - this._prefetchAt < 30000)) return;
            this._prefetchAbort?.abort();
            const controller = this._prefetchAbort = new AbortController();
            this._prefetchPaintAbort?.abort();
            const paint = this._prefetchPaintAbort = new AbortController();
            controller.signal.addEventListener("abort", () => paint.abort(), { once: true });
            this._prefetchKey = key;
            this._prefetchAt = Date.now();
            const engineReady = loadPdfEngine();
            engineReady?.catch(() => {});
            let revisionUrl;
            try {
                const quietWork = { post: (url, payload) => this.$work.post(url, payload, true) };
                const source = await resolvePdfSource(item, quietWork, this.$workUrl);
                if (controller.signal.aborted) return;
                const verifiedAlternate = source.revision ? async (hash) => {
                    if (hash !== source.revision.source_sha256) return null;
                    const content = await this.$work.get(`/pdf/revisions/${source.revision.id}/`, {}, true, null, { responseType: "blob", signal: controller.signal });
                    revisionUrl = URL.createObjectURL(content.data);
                    return revisionUrl;
                } : undefined;
                const prepared = await preparePdfDocument(source.url, engineReady, { signal: controller.signal, prefetchOnly: true, verifiedAlternate });
                if (prepared) {
                    const pdf = await prepared.ready;
                    const sourceHash = prepared.sourceSha256 || prepared.sha256;
                    const orientation = readScanOrientation(pdf, { article: item.articolo, drawing: item.disegno, source_sha256: sourceHash,
                        revision_id: source.revision && source.revision.source_sha256 === sourceHash ? source.revision.id : undefined }, quietWork);
                    await orientation;
                    await warmPdfPage(prepared, paint.signal, this.previewPublisher(item, prepared, source.revision, paint.signal));
                }
            } catch { /* Speculative loading never changes the current page or shows errors. */ }
            finally { if (revisionUrl) URL.revokeObjectURL(revisionUrl); }
        },
        previewPublisher(item, prepared, revision, signal) {
            const sourceHash = prepared.sourceSha256 || prepared.sha256;
            const verified = revision && sourceHash && revision.source_sha256 === sourceHash;
            const publish = (url) => {
                if (!signal.aborted) bus.$emit("pdf-preview-updated", {
                    article: item.articolo, drawing: item.disegno,
                    revision: verified ? revision.number : 0, url: verified || !revision ? url : "",
                });
            };
            if (revision && !verified) { publish(""); return undefined; }
            return publish;
        },
        async savedPdf({resume} = {}) {
            this._prefetchAbort?.abort();
            this._prefetchKey = "";
            const item = this.pdf.item;
            await this.showPdf(item,resume);
            const prepared = this.pdf.prepared, context = this.pdf.context;
            if (!this.pdf.overlay || !prepared || this.pdf.item !== item || !context?.displayed_revision) return;
            const controller = this._previewAbort = new AbortController();
            const publish = this.previewPublisher(item, prepared, { number: context.displayed_revision, source_sha256: context.source_sha256 }, controller.signal);
            try {
                const pdf = await prepared.ready;
                await readScanOrientation(pdf, context, this.$work);
                await renderPdfPreview(pdf, controller.signal, publish);
            }
            catch { /* The saved PDF remains available even if its inline image cannot be painted. */ }
        },
        PdfopenHandler(PDFViewerApplication) {
            this.$emit("open", PDFViewerApplication);
        },
        PdfRendered() {
            this.pdfloading = false;
            console.log("[PDF] Prima pagina visibile in", Math.round(performance.now() - this._pdfStartedAt), "ms");
        },
        pdfResponseErrorDetail: async function (error, fallback) {
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const payload = JSON.parse(await data.text());
                    return payload.detail || payload.message || fallback;
                } catch (parseError) {
                    return error.message || fallback;
                }
            }
            return data?.detail || data?.message || error?.message || fallback;
        },
        showPdf: async function (item,resumeState = null) {
            if (typeof item === "string") item = /^(blob:|https?:\/\/)/i.test(item) ? { minio: item } : item.startsWith("/") ? { oseon: item } : { pdf: item };
            item = item || {};
            if (this.pdf.overlay && this.$refs?.dialog?.requestClose && !this.$refs.dialog.requestClose(() => this.showPdf(item))) return;
            this._prefetchPaintAbort?.abort();
            this._previewAbort?.abort();
            if (!item.articolo || !item.disegno || this._prefetchKey !== `${item.articolo}:${item.disegno}`) this._prefetchAbort?.abort();
            this._pdfPreparation?.abort();
            const preparation = this._pdfPreparation = new AbortController();
            this.pdf.prepared?.cancel()?.catch(() => {});
            if (this.pdf.revokeOnClose && this.pdf.url) URL.revokeObjectURL(this.pdf.url);
            const requestId = ++this.pdf.instanceKey;
            const continuing=!!resumeState && this.pdf.overlay;
            this.pdf.item = item;
            this.pdf.resumeState=resumeState;
            this.pdf.url = undefined;
            this.pdf.error = "";
            this.pdf.revokeOnClose = item.revokeOnClose === true;
            this.pdf.overlay = continuing;
            this.pdf.pending = !continuing;
            this.pdf.prepared = null;
            this.pdf.title = item.disegno || item.title || "Anteprima PDF";
            this.pdfloading = true;
            this._pdfStartedAt = performance.now();
            // Import the engine concurrently with the document lookup.
            const engineReady = loadPdfEngine();
            engineReady.catch(() => {});
            let source;
            try {
                source = await resolvePdfSource(item, this.$work, this.$workUrl);
                if (requestId !== this.pdf.instanceKey) {
                    if (source.revokeOnClose) URL.revokeObjectURL(source.url);
                    return;
                }
                const revision = source.revision;
                const verifiedAlternate = revision && !item.originalPdf ? async (hash) => {
                    if (revision.source_sha256 !== hash || requestId !== this.pdf.instanceKey) return null;
                    const content = await this.$work.get(`/pdf/revisions/${revision.id}/`, {}, true, null, { responseType: "blob", signal: preparation.signal });
                    source = { url: URL.createObjectURL(content.data), revokeOnClose: true };
                    return source.url;
                } : undefined;
                const prepared = await preparePdfDocument(source.url, engineReady, { signal: preparation.signal, verifiedAlternate });
                const sourceHash = prepared.sourceSha256 || prepared.sha256;
                if (requestId !== this.pdf.instanceKey) {
                    await prepared.cancel();
                    if (source.revokeOnClose) URL.revokeObjectURL(source.url);
                    return;
                }
                this.pdf.prepared = prepared;
                this.pdf.context = item.articolo && item.disegno ? Object.freeze({ article: item.articolo, drawing: item.disegno,
                    source_sha256: sourceHash, revision: revision?.number || 0,
                    original: item.originalPdf === true,
                    displayed_revision: !item.originalPdf && revision && revision.source_sha256 === sourceHash ? revision.number : 0,
                    revision_id: !item.originalPdf && revision && revision.source_sha256 === sourceHash ? revision.id : undefined }) : null;
                this._pdfPreparation = null;
                this.pdf.url = source.url;
                this.pdf.revokeOnClose = source.revokeOnClose;
                this.pdf.pending = false;
                this.pdf.mounted = true;
                this.pdf.overlay = true;
                this._pdfUrlReadyAt = performance.now();
                console.log("[PDF] File pronto in", Math.round(this._pdfUrlReadyAt - this._pdfStartedAt), "ms");
            } catch (error) {
                if (source?.revokeOnClose && this.pdf.url !== source.url) URL.revokeObjectURL(source.url);
                if (requestId !== this.pdf.instanceKey) return;
                const detail = await this.pdfResponseErrorDetail(error, "Anteprima PDF non disponibile. Riprova.");
                if (requestId !== this.pdf.instanceKey) return;
                this.pdf.error = detail;
                this.pdf.pending = false;
                this.pdf.mounted = true;
                this.pdf.overlay = true;
                this.pdfloading = false;
            }
        },
        async retryPdf() {
            if (this.$refs?.dialog?.requestClose && !this.$refs.dialog.requestClose(() => this.retryPdf())) return;
            clearPdfDocuments();
            const requestId = ++this.pdf.instanceKey;
            this.pdf.mounted = false;
            await this.$nextTick();
            if (requestId !== this.pdf.instanceKey || !this.pdf.overlay) return;
            this.pdf.mounted = true;
            if (!this.pdf.url) return this.showPdf(this.pdf.item);
            this.pdf.error = "";
            this._pdfStartedAt = performance.now();
            this._pdfUrlReadyAt = this._pdfStartedAt;
            loadPdfEngine().catch(() => {
                if (requestId === this.pdf.instanceKey && this.pdf.overlay) this.pdf.error = "Impossibile caricare il visualizzatore. Riprova.";
            });
        },
        closePdf() {
            if (this.$refs?.dialog?.requestClose && !this.$refs.dialog.requestClose()) return;
            this._prefetchAbort?.abort();
            this._prefetchPaintAbort?.abort();
            this._pdfPreparation?.abort();
            this._pdfPreparation = null;
            this.pdf.prepared?.cancel()?.catch(() => {});
            this.pdf.prepared = null;
            this.pdf.pending = false;
            // Invalidate pending lookups before dismissing the document.
            this.pdf.instanceKey += 1;
            this.pdf.overlay = false;
            const ownedUrl = this.pdf.revokeOnClose ? this.pdf.url : null;
            this.pdf.url = undefined;
            this.pdf.revokeOnClose = false;
            this.pdfloading = false;
            this.pdf.error = "";
            this.pdf.item = null;
            this.pdf.context = null;
            this.pdf.resumeState=null;
            // Let Vue release the displayed document before revoking its owned blob.
            this.$nextTick(() => { if (ownedUrl) URL.revokeObjectURL(ownedUrl); });
        },
    },
};
</script>

<style scoped>
.work-pdf-pending { position: fixed; right: 16px; bottom: 16px; z-index: 99999; display: flex; gap: 16px; padding: 12px 16px; border-radius: 4px; background: #1a2327; color: #edf4f5; }
.work-pdf-pending button { color: #70ced7; text-decoration: underline; }
</style>
